/**
 * 공통 메모 요약 — 현장 관리 헤더의 〈전체 공지사항〉 버튼에 붙는 최신 배지.
 *
 * 왜 필요한가: 날짜 없는 메모(`scheduled_at IS NULL`)는 캘린더의 기간 조회에
 * 걸리지 않아, 버튼을 눌러 들어가 보지 않으면 누가 무엇을 적어뒀는지 알 수
 * 없다. 버튼 자체가 "최근에 새로 올라온 게 있는지"를 말해 주면 들어가 볼
 * 이유가 생긴다. (건수는 표시하지 않기로 해 배지 하나만 남았다.)
 *
 * 요약 전용 API 가 없어 전체 일정을 받아 클라이언트에서 본다. 같은 페이지의
 * MemoBoard 도 같은 호출을 하므로 캘린더 탭이 열려 있으면 요청이 두 번
 * 나간다 — 요약 엔드포인트가 생기면 여기만 바꾸면 된다.
 */
import { useEffect, useState } from 'react';

import { SiteScheduleService } from '../../../services/SiteScheduleService';
import { hasScheduleDate } from '../../../models/SiteSchedule';

/** 배지를 띄우는 기준 — "최근"은 1주로 본다. */
export const RECENT_DAYS = 7;

export interface MemoSummary {
  /** 최근 RECENT_DAYS 안에 등록된 메모가 하나라도 있는가. 조회 실패·로딩 중이면 false. */
  hasRecent: boolean;
}

/**
 * 조회 실패는 조용히 삼킨다 — 헤더 버튼의 곁가지 정보라, 여기서 에러를
 * 띄우면 정작 중요한 화면을 덮는다. 그때는 배지 없이 버튼만 보인다.
 */
export default function useMemoSummary(): MemoSummary {
  const [summary, setSummary] = useState<MemoSummary>({ hasRecent: false });

  useEffect(() => {
    let cancelled = false;
    // 기간 인자를 주지 않는다 — scheduled_at 이 NULL 인 행까지 받아야 한다.
    SiteScheduleService.listAll()
      .then((list) => {
        if (cancelled) return;
        const memos = list.filter((s) => !hasScheduleDate(s));
        const since = Date.now() - RECENT_DAYS * 24 * 60 * 60 * 1000;
        setSummary({
          hasRecent: memos.some((m) => {
            const t = new Date(m.created_at).getTime();
            // created_at 이 비거나 깨진 값이면 NaN 이 되고, NaN > since 는 항상
            // false 라 배지가 뜨지 않는다 — 조용히 무시되는 게 맞다.
            return t > since;
          }),
        });
      })
      .catch(() => {
        if (!cancelled) setSummary({ hasRecent: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return summary;
}
